import {
  sanitizeContent,
  csvToFile,
} from 'src/util/csv';
import { CLOUD_FETCH_TIMEOUT_MS } from 'src/sections/UploadSurvey/constants';

const GOOGLE_SHEETS_EXPORT_URL = 'https://docs.google.com/spreadsheets/d';

export default class GoogleDriveService {

  extractFileId(url) {
    if (!url || typeof url !== 'string') {
      return null;
    }

    const sheetsMatch = url.match(
      /docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/
    );
    if (sheetsMatch) {
      return sheetsMatch[1];
    }

    return null;
  }

  isValidGoogleUrl(url) {
    return this.extractFileId(url) !== null;
  }

  buildSheetsExportUrl(fileId) {
    return `${GOOGLE_SHEETS_EXPORT_URL}/${fileId}/export?format=csv`;
  }

  /**
   * Busca e processa o conteúdo CSV de uma planilha do Google Sheets.
   *
   * Constrói URL de exportação, faz requisição HTTP GET com timeout,
   * valida permissões e retorna o conteúdo CSV sanitizado.
   *
   * @param {string} fileId - ID único do arquivo do Google Sheets extraído da URL
   * @returns {Promise<string>} Conteúdo CSV sanitizado da planilha
   */
  async fetchCSVFromSheets(fileId) {
    const url = this.buildSheetsExportUrl(fileId);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CLOUD_FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Arquivo sem permissão de acesso. Verifique se está compartilhado publicamente.');
        }
        if (response.status === 404) {
          throw new Error('Arquivo não encontrado. Verifique se o link está correto.');
        }
        throw new Error(`Erro ao baixar arquivo: ${response.status}`);
      }

      const text = await response.text();

      if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
        throw new Error('Arquivo sem permissão de acesso. Verifique se está compartilhado publicamente.');
      }

      return sanitizeContent(text);
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw new Error('Tempo limite excedido. Verifique sua conexão e tente novamente.');
      }

      throw error;
    }
  }

  async importFromUrl(url) {
    const fileId = this.extractFileId(url);

    if (!fileId) {
      throw new Error('URL do Google inválida. Use um link do Google Sheets.');
    }

    const csvContent = await this.fetchCSVFromSheets(fileId);

    const shortId = fileId.substring(0, 8);
    const fileName = `google-import-${shortId}.csv`;

    const file = csvToFile(csvContent, fileName);

    return { file, fileName };
  }
}

export const googleDriveService = new GoogleDriveService();
